/**
 * Get the key of an object
 *
 * @param {Object} obj
 * The object to search in
 *
 * @param {String} value
 * The value to search for
 */
const getKey = (obj: any, value: string, options: null | string = null): any => {
  if (value === 'placeOfPurchase' && options) {
    return 'select'
  }

  return obj ? Object.keys(obj).find((x: any) => obj[x].includes(value) || obj[x] === 'default' || null) : null
}

/**
 * Gets the current device ID
 */
const getDeviceId = (): Promise<string> => {
  const F2: any = require('fingerprintjs2')
  return new Promise((res) => {
    F2.get((components: object[]) => {
      const values = components.map((component: { value: object }) => component.value)
      const ID = F2.x64hash128(values.join(''), 31)
      res(ID)
    })
  })
}
/**
 * Gets the current session ID
 *
 * @param {String} deviceID
 * The current device ID
 *
 * @param {String} submissionBaseUri
 * The submissionUri set in env variable
 */
const getSessionId = async (deviceID: any, submissionBaseUri: string) => {
  const url = `${submissionBaseUri}/getSession`

  const res = await fetch(url, {
    headers: { 'Content-Type': 'application/json' },
    body: `fingerprint=${deviceID}`,
    method: 'POST',
  })

  return res.text()
}

/**
 * Gets a signed url for allowing uploads to an S3 bucket
 *
 * @param {String} submissionBaseUri
 * The submissionUri set in env variable
 *
 * @param {String} campaignId
 * The current campaign ID
 *
 * @param {String} field
 * The current field
 *
 * @param {String} filename
 * The filename of the uploaded file
 */
const getSignedUrl = async (
  submissionBaseUri: string,
  campaignId: string,
  field: string,
  filename: string,
  type: string
) => {
  const url = `${submissionBaseUri}/getUploadURI?campaignId=${encodeURIComponent(
    campaignId
  )}&filename=${encodeURIComponent(filename)}&fieldName=${encodeURIComponent(field)}&type=${type}`

  const signedResponse: any = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  })

  const signedJSON = await signedResponse.json()

  return {
    uploadURL: signedJSON.uploadURL,
    uploadFilename: signedJSON.uploadFilename,
  }
}

/**
 * Get a search parameter from the url
 *
 * @param {String} param
 * The parameter to get
 */
const getSearchParam = (param: string, query?: string) => {
  return new URLSearchParams(query || window.location.search).get(param)
}

/**
 * Get the mouse
 * @param e
 * @param el
 */
const getMousePos = (e: any, el: any) => {
  const { top, left } = el.getBoundingClientRect()
  const scrollTop = window.pageYOffset || document.documentElement.scrollTop
  const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

  return {
    x: (e.pageX || e.touches[0].clientX) - left - scrollLeft,
    y: (e.pageY || e.touches[0].clientY) - top - scrollTop,
  }
}

/**
 * Get the distance between two mouse co-ordinates
 *
 * @param {Object} one
 * The first mouse event {x, y}
 *
 * @param {Object} two
 * The second mouse event {x, y}
 */
const getDistance = (one: any, two: any) => Math.sqrt(Math.pow(two.x - one.x, 2) + Math.pow(two.y - one.y, 2))

/**
 * Get the angle between two mouse co-ordinates
 *
 * @param {Object} one
 * The first mouse event {x, y}
 *
 * @param {Object} two
 * The second mouse event {x, y}
 */
const getAngle = (one: any, two: any) => Math.atan2(two.x - one.x, two.y - one.y)

export { getKey, getDeviceId, getSessionId, getSignedUrl, getSearchParam, getMousePos, getDistance, getAngle }
